import { LCD } from "../../config";
import type { PlayState } from "../../core/game";
import { drawBlockAt, drawFoodAt, drawWallFrame } from "./blocks";
import { drawText, type DrawContext } from "../text";

/** Oyun ekranının gösterdiği ayar durumu — mantık ayarlarından ayrı görünüm verisi. */
export interface GameViewData {
  difficultyLabel: string;
  wallPass: boolean;
  sound: boolean;
}

/** Oyun ekranı: skor şeridi + oyun alanı. */
export function drawGame(rc: DrawContext, play: PlayState, view: GameViewData, hiScore: number): void {
  drawHeader(rc, play, view, hiScore);
  drawPlayfield(rc, play, view.wallPass);
}

/** Duraklatma perdesi (oyun alanı üzerinde yarı saydam). */
export function drawPause(rc: DrawContext): void {
  const { width, height, cell } = rc.layout;
  rc.ctx.fillStyle = "rgba(155, 187, 69, 0.6)";
  rc.ctx.fillRect(0, 0, width, height);
  drawText(rc, "DURAKLADI", width / 2, height * 0.42, Math.floor(cell * 0.9));
  drawText(rc, "DEVAM: A/ENTER", width / 2, height * 0.55, Math.floor(cell * 0.5));
  drawText(rc, "MENU: B/ESC", width / 2, height * 0.62, Math.floor(cell * 0.5));
}

function drawHeader(rc: DrawContext, play: PlayState, view: GameViewData, hiScore: number): void {
  const { cell, headerH, offX, offY } = rc.layout;
  const fieldWidth = play.grid.cols * cell;
  const cy = offY + headerH / 2 - cell * 0.2;

  drawText(
    rc,
    `SKOR ${String(play.score).padStart(4, "0")}`,
    offX,
    cy,
    Math.floor(cell * 0.55),
    "left",
  );
  drawText(
    rc,
    `HI ${String(hiScore).padStart(4, "0")}`,
    offX + fieldWidth,
    cy,
    Math.floor(cell * 0.55),
    "right",
  );

  // Durum satırı: zorluk harfi / duvar durumu / ses durumu
  const statusY = offY + headerH - cell * 0.35;
  const status = [
    view.difficultyLabel.charAt(0),
    view.wallPass ? "∞" : "□",
    view.sound ? "♪" : "×",
  ].join("  ");
  drawText(rc, status, offX + fieldWidth / 2, statusY, Math.floor(cell * 0.42));
}

function drawPlayfield(rc: DrawContext, play: PlayState, wallPass: boolean): void {
  const { cell, headerH, offX, offY } = rc.layout;
  const px = offX;
  const py = offY + headerH;
  const fieldW = play.grid.cols * cell;
  const fieldH = play.grid.rows * cell;
  const { ctx } = rc;

  // Oyun alanı zemini (LCD'nin kalanından ayrışsın diye soluk taban)
  ctx.fillStyle = LCD.mid;
  ctx.globalAlpha = 0.25;
  ctx.fillRect(px, py, fieldW, fieldH);
  ctx.globalAlpha = 1;

  drawWallFrame(rc, play.grid, wallPass);

  // Yem — sabit, yanıp sönmez
  drawFoodAt(rc, play.food.x, play.food.y);

  // Yılan: kuyruktan kafaya, kafa dolu blok
  for (let i = play.snake.length - 1; i >= 0; i--) {
    drawBlockAt(rc, play.snake[i].x, play.snake[i].y, i === 0 ? 0 : 1);
  }
  drawSnakeEyes(rc, play);
}

/** Oyun alanını (yem + yılan) çizer; game over perdesinin altında da kullanılır. */
export { drawPlayfield };

/** Kafa bloğuna yönü gösteren iki göz pikseli çizer. */
function drawSnakeEyes(rc: DrawContext, play: PlayState): void {
  const head = play.snake[0];
  const { cell, headerH, offX, offY } = rc.layout;
  const px = offX + head.x * cell;
  const py = offY + headerH + head.y * cell;
  const { dir } = play;

  const cx = px + cell / 2 + dir.x * cell * 0.2;
  const cy = py + cell / 2 + dir.y * cell * 0.2;
  const perp = dir.x !== 0 ? { x: 0, y: 1 } : { x: 1, y: 0 };
  const eyeSize = cell * 0.16;
  const offset = cell * 0.2;

  rc.ctx.fillStyle = LCD.bg;
  rc.ctx.fillRect(cx + perp.x * offset - eyeSize / 2, cy + perp.y * offset - eyeSize / 2, eyeSize, eyeSize);
  rc.ctx.fillRect(cx - perp.x * offset - eyeSize / 2, cy - perp.y * offset - eyeSize / 2, eyeSize, eyeSize);
}
